import { createContext, useContext, useReducer, useCallback, useEffect, useState, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import api from '../services/api';
import ablyService from '../services/ably';
import { isApiAuthReady } from '../utils/apiReady';
import {
  IDLE_DELIVERY,
  PLAYLIST_DRAFT_KEY,
  PLAYLIST_MAX_ITEMS,
  UNSAVED,
  buildSavePlaylistPayload,
  createEmptyPlaylist,
  createPlaylistItem,
  movePlaylistItem as movePlaylistItems,
  newPlaylistId,
  parseDraft,
  playlistFromSaved,
  serializeDraft,
  sortSavedPlaylists,
} from '../utils/playlist';
import {
  DELIVERY_ACTIVE,
  classifyBuildError,
  classifySendError,
  isStationOnline,
  retryModeFor,
  stationLabel,
} from '../utils/playlistDelivery';
import { IMPORT_QUEUE_STATE_TYPE } from '../utils/stationImport';
import { failureLine } from '../components/features/playlist/playlistCopy';

// Create context
const AppContext = createContext();

// Initial state
const initialState = {
  designs: [],
  selectedDesigns: [],
  completedDesigns: [],
  // Per-(designId, agentId) progress map driven by Ably `dispatch_progress` events.
  // Shape: { [designId]: { [agentId]: { stage, stageLabel, stageIndex, stageTotal, detail, startedAt, errorMessage?, failedAtStage? } } }
  dispatchProgress: {},
  // Set of `${designId}:${agentId}` pairs whose dispatch has been RESOLVED by the
  // authoritative n8n webhook HTTP response (success or failure). Once resolved, a
  // pair is durably terminal: any later Ably (`fromAbly`) progress event for it is
  // ignored, so an informational straggler can never re-create or override the
  // terminal state. Cleared only when a NEW dispatch is started for that pair.
  resolvedDispatches: {},
  linkedAgents: [],
  settings: {
    theme: 'dark'
  },
  autoDispatch: {
    enabled: false,
    schedule: null,
    lastRunAt: null,
    isLoading: false,
  },
  canvaAuthFailure: null,
  // The org's plan from src/constants/plans.js, or null until a workflow reports it.
  orgPlan: null,
  // Per-Media Station settings (T042). Shape:
  // { [agentId]: { libraryName, settingsUpdatedAt, lastSave: {…}|null, station: <station_settings_state>|null } }
  // `station` is the latest station-reported state (Ably or the POST response);
  // `lastSave` is the outcome of the last Save from this browser.
  stationSettings: {},
  // ms-pp21-22 L4: each v22 station's latest `import_queue_state` report.
  stationImports: {},
  // 'idle' | 'loading' | 'ready' | 'not_configured' (fetch 404) | 'error'
  stationSettingsStatus: 'idle',
  isSyncingNow: false,
  designsLastFetchedAt: null,
  isLoading: false,
  isDesignsLoading: false,
  isAgentsLoading: false,
  error: null,
  canvaReauthRequired: false,
  // Service Playlist (T240). Item order is the delivered order.
  playlist: createEmptyPlaylist(),
  // savedAt of a non-empty draft restored from localStorage at startup; null once dismissed.
  playlistRestoredFrom: null,
  // True when the page was reloaded mid-delivery (banner B-2); delivery state itself is never restored.
  playlistInterrupted: false,
  // Saved playlists (T240 M3): rows as the Playlists webhook returns them.
  savedPlaylists: [],
  // 'idle' | 'loading' | 'ready' | 'not_configured' (webhook inactive) | 'error'
  savedPlaylistsStatus: 'idle',
};

const PLAYLIST_DELIVERING_KEY = 'turbosync:playlist-delivering:v1';

const hydratePlaylistDraft = (base) => {
  try {
    const interrupted = window.localStorage.getItem(PLAYLIST_DELIVERING_KEY) !== null;
    const draft = parseDraft(window.localStorage.getItem(PLAYLIST_DRAFT_KEY));
    if (!draft) return { ...base, playlistInterrupted: interrupted };
    return {
      ...base,
      playlist: draft.playlist,
      playlistRestoredFrom: draft.playlist.items.length > 0 && !interrupted ? draft.savedAt : null,
      playlistInterrupted: interrupted,
    };
  } catch {
    return base;
  }
};

// Editing a delivered playlist returns the strip to idle with R-2; the last
// delivery time stays for the re-delivery note (R-1).
const afterEdit = (delivery) => (delivery.status === 'done'
  ? { ...IDLE_DELIVERY, agentId: delivery.agentId, deliveredAt: delivery.deliveredAt, edited: true }
  : delivery);

const withPlaylist = (state, changes, { dirty = true } = {}) => ({
  ...state,
  playlist: {
    ...state.playlist,
    ...changes,
    ...(dirty ? { dirty: true, delivery: afterEdit(state.playlist.delivery) } : {}),
  },
});

// Reducer function
export function appReducer(state, action) {
  switch (action.type) {
    case 'SET_DESIGNS':
      return { ...state, designs: action.payload, isDesignsLoading: false };
    
    case 'SET_LINKED_AGENTS':
      return { ...state, linkedAgents: action.payload, isAgentsLoading: false };
    
    case 'TOGGLE_DESIGN_SELECTION': {
      const designId = action.payload;
      return {
        ...state,
        selectedDesigns: state.selectedDesigns.includes(designId)
          ? state.selectedDesigns.filter(id => id !== designId)
          : [...state.selectedDesigns, designId]
      };
    }
    
    case 'SELECT_ALL_DESIGNS':
      return {
        ...state,
        selectedDesigns: state.designs.map(d => d.design_id || d.id)
      };
    
    case 'CLEAR_SELECTION':
      return { ...state, selectedDesigns: [] };
    
    case 'ADD_COMPLETED_DESIGN':
      if (state.completedDesigns.includes(action.payload)) return state;
      return {
        ...state,
        completedDesigns: [...state.completedDesigns, action.payload]
      };
    
    case 'REMOVE_COMPLETED_DESIGN':
      return {
        ...state,
        completedDesigns: state.completedDesigns.filter(id => id !== action.payload)
      };
    
    case 'DISPATCH_PROGRESS_UPDATE': {
      const { designId, agentId, stage, stageLabel, stageIndex, stageTotal, detail, fromAbly } = action.payload;
      // Ably is informational only: it may never resurrect a dispatch the HTTP
      // response already resolved. Local writes (queued seed, HTTP-driven) omit
      // `fromAbly` and are always applied.
      if (fromAbly && state.resolvedDispatches[`${designId}:${agentId}`]) return state;
      const designEntry = state.dispatchProgress[designId] || {};
      const prev = designEntry[agentId];
      return {
        ...state,
        dispatchProgress: {
          ...state.dispatchProgress,
          [designId]: {
            ...designEntry,
            [agentId]: {
              ...(prev || {}),
              stage,
              stageLabel,
              stageIndex,
              stageTotal,
              detail,
              startedAt: prev?.startedAt || Date.now(),
              errorMessage: undefined,
              failedAtStage: undefined,
            },
          },
        },
      };
    }

    case 'DISPATCH_DONE': {
      const { designId, agentId, stageLabel, stageIndex, stageTotal, detail, fromAbly } = action.payload;
      if (fromAbly && state.resolvedDispatches[`${designId}:${agentId}`]) return state;
      const designEntry = state.dispatchProgress[designId] || {};
      const prev = designEntry[agentId];
      return {
        ...state,
        dispatchProgress: {
          ...state.dispatchProgress,
          [designId]: {
            ...designEntry,
            [agentId]: {
              ...(prev || {}),
              stage: 'done',
              stageLabel,
              stageIndex,
              stageTotal,
              detail,
              startedAt: prev?.startedAt || Date.now(),
              errorMessage: undefined,
              failedAtStage: undefined,
            },
          },
        },
      };
    }

    case 'DISPATCH_FAILED': {
      const { designId, agentId, stageLabel, stageIndex, stageTotal, detail, errorMessage, failedAtStage, fromAbly } = action.payload;
      if (fromAbly && state.resolvedDispatches[`${designId}:${agentId}`]) return state;
      const designEntry = state.dispatchProgress[designId] || {};
      const prev = designEntry[agentId];
      return {
        ...state,
        dispatchProgress: {
          ...state.dispatchProgress,
          [designId]: {
            ...designEntry,
            [agentId]: {
              ...(prev || {}),
              stage: 'failed',
              stageLabel,
              stageIndex,
              stageTotal,
              detail,
              startedAt: prev?.startedAt || Date.now(),
              errorMessage,
              failedAtStage,
            },
          },
        },
      };
    }

    case 'CLEAR_DISPATCH_PROGRESS': {
      const { designId, agentId } = action.payload;
      const designEntry = state.dispatchProgress[designId];
      if (!designEntry) return state;
      if (!agentId) {
        const next = { ...state.dispatchProgress };
        delete next[designId];
        return { ...state, dispatchProgress: next };
      }
      const nextDesignEntry = { ...designEntry };
      delete nextDesignEntry[agentId];
      const next = { ...state.dispatchProgress };
      if (Object.keys(nextDesignEntry).length === 0) {
        delete next[designId];
      } else {
        next[designId] = nextDesignEntry;
      }
      return { ...state, dispatchProgress: next };
    }

    case 'DISPATCH_RESOLVED': {
      // The webhook HTTP response settled (success or failure) — the sole
      // authority on completion. Mark the pair durably terminal so no later Ably
      // event can re-create or override it.
      const { designId, agentId } = action.payload;
      return {
        ...state,
        resolvedDispatches: { ...state.resolvedDispatches, [`${designId}:${agentId}`]: true },
      };
    }

    case 'DISPATCH_UNRESOLVE': {
      // A NEW dispatch is starting for this pair — lift the terminal lock so its
      // own progress events flow again.
      const { designId, agentId } = action.payload;
      const key = `${designId}:${agentId}`;
      if (!state.resolvedDispatches[key]) return state;
      const next = { ...state.resolvedDispatches };
      delete next[key];
      return { ...state, resolvedDispatches: next };
    }

    case 'UPDATE_AGENT_STATUS':
      return {
        ...state,
        linkedAgents: state.linkedAgents.map(agent =>
          agent.agent_id === action.payload.agentId
            ? { ...agent, status: action.payload.status }
            : agent
        )
      };
    
    case 'ADD_AGENT':
      return {
        ...state,
        linkedAgents: [...state.linkedAgents, action.payload]
      };
    
    case 'REMOVE_AGENT':
      return {
        ...state,
        linkedAgents: state.linkedAgents.filter(
          agent => agent.agent_id !== action.payload
        )
      };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_DESIGNS_LOADING':
      return { ...state, isDesignsLoading: action.payload };
    
    case 'SET_AGENTS_LOADING':
      return { ...state, isAgentsLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false, isDesignsLoading: false, isAgentsLoading: false };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    case 'SET_CANVA_REAUTH_REQUIRED':
      return { ...state, canvaReauthRequired: true, isDesignsLoading: false };
    
    case 'CLEAR_CANVA_REAUTH':
      return { ...state, canvaReauthRequired: false };
    
    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: { ...state.settings, ...action.payload }
      };

    case 'SET_AUTO_DISPATCH':
      return {
        ...state,
        autoDispatch: { ...state.autoDispatch, ...action.payload, isLoading: false }
      };

    case 'SET_CANVA_AUTH_FAILURE':
      return { ...state, canvaAuthFailure: action.payload };

    case 'SET_ORG_PLAN':
      return action.payload ? { ...state, orgPlan: action.payload } : state;

    case 'SET_AUTO_DISPATCH_LOADING':
      return {
        ...state,
        autoDispatch: { ...state.autoDispatch, isLoading: action.payload }
      };

    case 'SET_SYNCING_NOW':
      return { ...state, isSyncingNow: action.payload };

    case 'CLEAR_DESIGN_FLAGS_LOCAL': {
      const ids = new Set(action.payload);
      return {
        ...state,
        designs: state.designs.map(d =>
          ids.has(d.design_id || d.id) ? { ...d, is_new: false, is_updated: false } : d
        )
      };
    }

    case 'SET_DESIGNS_LAST_FETCHED':
      return { ...state, designsLastFetchedAt: action.payload };

    case 'SET_STATION_SETTINGS_STATUS':
      return { ...state, stationSettingsStatus: action.payload };

    case 'SET_STATION_SETTINGS': {
      // payload: stations[] from fetch-station-settings
      const next = { ...state.stationSettings };
      (action.payload || []).forEach((s) => {
        if (!s?.agent_id) return;
        next[s.agent_id] = {
          lastSave: null,
          station: null,
          ...next[s.agent_id],
          libraryName: s.library_name ?? null,
          settingsUpdatedAt: s.settings_updated_at ?? null,
        };
      });
      return { ...state, stationSettings: next, stationSettingsStatus: 'ready' };
    }

    case 'STATION_SETTINGS_SAVED': {
      // A Save settled. `station` is replaced (not merged) by the POST's
      // station_state so an older station report can't masquerade as an ack
      // for this save; a later Ably state message updates it again.
      const { agentId, libraryName, settingsUpdatedAt, lastSave, station } = action.payload;
      const prev = state.stationSettings[agentId] || {};
      const saved = !lastSave?.error;
      return {
        ...state,
        stationSettings: {
          ...state.stationSettings,
          [agentId]: {
            ...prev,
            libraryName: saved ? libraryName : (prev.libraryName ?? null),
            settingsUpdatedAt: saved ? (settingsUpdatedAt ?? null) : (prev.settingsUpdatedAt ?? null),
            lastSave,
            station: saved ? (station ?? null) : (prev.station ?? null),
          },
        },
      };
    }

    case 'STATION_SETTINGS_STATE': {
      const { agentId, state: stationState } = action.payload;
      const prev = state.stationSettings[agentId] || { libraryName: null, settingsUpdatedAt: null, lastSave: null };
      return {
        ...state,
        stationSettings: {
          ...state.stationSettings,
          [agentId]: { ...prev, station: stationState },
        },
      };
    }

    case 'STATION_IMPORT_STATE': {
      const { agentId, state: report } = action.payload;
      const prev = state.stationImports?.[agentId];
      if (prev && Date.parse(prev.at) > Date.parse(report.at)) return state;
      return { ...state, stationImports: { ...state.stationImports, [agentId]: report } };
    }

    case 'PLAYLIST_ADD_ITEM': {
      const { item, playlistId } = action.payload;
      if (state.playlist.items.length >= PLAYLIST_MAX_ITEMS) return state;
      return withPlaylist(state, {
        id: state.playlist.id ?? playlistId,
        items: [...state.playlist.items, item],
      });
    }

    case 'PLAYLIST_REMOVE_ITEM': {
      const items = state.playlist.items.filter(item => item.itemKey !== action.payload.itemKey);
      if (items.length === state.playlist.items.length) return state;
      return withPlaylist(state, { items });
    }

    case 'PLAYLIST_RESTORE_ITEM': {
      const { item, index } = action.payload;
      const { items } = state.playlist;
      if (items.length >= PLAYLIST_MAX_ITEMS || items.some(i => i.itemKey === item.itemKey)) return state;
      const at = Math.min(Math.max(index, 0), items.length);
      return withPlaylist(state, { items: [...items.slice(0, at), item, ...items.slice(at)] });
    }

    case 'PLAYLIST_MOVE_ITEM': {
      const { from, to } = action.payload;
      const items = movePlaylistItems(state.playlist.items, from, to);
      if (items === state.playlist.items) return state;
      return withPlaylist(state, { items });
    }

    case 'PLAYLIST_SET_ORDER': {
      const { itemKeys } = action.payload;
      const byKey = new Map(state.playlist.items.map(item => [item.itemKey, item]));
      if (itemKeys.length !== byKey.size || !itemKeys.every(key => byKey.has(key))) return state;
      if (itemKeys.every((key, i) => state.playlist.items[i].itemKey === key)) return state;
      return withPlaylist(state, { items: itemKeys.map(key => byKey.get(key)) });
    }

    case 'PLAYLIST_SET_META': {
      const changes = Object.fromEntries(
        ['name', 'serviceDate', 'targetAgentId']
          .filter(key => action.payload[key] !== undefined)
          .map(key => [key, action.payload[key]])
      );
      return withPlaylist(state, changes);
    }

    case 'PLAYLIST_SET':
      return { ...state, playlist: action.payload.playlist, playlistRestoredFrom: null };

    case 'PLAYLIST_CLEAR':
      return { ...state, playlist: action.payload?.playlist ?? createEmptyPlaylist(), playlistRestoredFrom: null };

    case 'PLAYLIST_DELIVERY_UPDATE':
      return withPlaylist(state, { delivery: { ...state.playlist.delivery, ...action.payload } }, { dirty: false });

    case 'PLAYLIST_DISMISS_INTERRUPTED':
      return { ...state, playlistInterrupted: false };

    case 'PLAYLIST_DISMISS_RESTORED':
      return { ...state, playlistRestoredFrom: null };

    // A save finished. `basis` is the draft that was sent: the draft stays
    // dirty if it was edited while the save was in flight.
    case 'PLAYLIST_MARK_SAVED': {
      const { basis, row } = action.payload;
      const current = state.playlist;
      if (current.id !== basis.id) return state;
      const unchanged = current.items === basis.items
        && current.name === basis.name
        && current.serviceDate === basis.serviceDate
        && current.targetAgentId === basis.targetAgentId;
      return {
        ...state,
        playlist: {
          ...current,
          id: row.id,
          dirty: unchanged ? false : current.dirty,
          savedUpdatedAt: row.updated_at ?? new Date().toISOString(),
          savedStatus: row.status ?? current.savedStatus ?? 'draft',
          lastDeliveredAt: row.last_delivered_at ?? current.lastDeliveredAt,
        },
      };
    }

    // The saved copy is gone (deleted here or elsewhere): the draft stays, unsaved.
    case 'PLAYLIST_MARK_UNSAVED':
      if (state.playlist.id !== action.payload.id) return state;
      return { ...state, playlist: { ...state.playlist, ...UNSAVED, dirty: true } };

    case 'SET_SAVED_PLAYLISTS':
      return { ...state, savedPlaylists: sortSavedPlaylists(action.payload), savedPlaylistsStatus: 'ready' };

    case 'SET_SAVED_PLAYLISTS_STATUS':
      return { ...state, savedPlaylistsStatus: action.payload };

    case 'UPSERT_SAVED_PLAYLIST': {
      const row = action.payload;
      const rest = state.savedPlaylists.filter((p) => p.id !== row.id);
      return { ...state, savedPlaylists: sortSavedPlaylists([...rest, row]) };
    }

    case 'REMOVE_SAVED_PLAYLIST':
      return { ...state, savedPlaylists: state.savedPlaylists.filter((p) => p.id !== action.payload) };

    default:
      return state;
  }
}

/**
 * App Provider Component
 * Wraps the app and provides global state and actions
 */
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState, hydratePlaylistDraft);
  const { getToken, isSignedIn } = useAuth();
  const [isApiReady, setIsApiReady] = useState(false);

  // DEV-only test handle: allows Cypress to inject reducer actions without
  // needing Canva connected or real Ably events. Never ships to production.
  if (import.meta.env.DEV) {
    window.__turboSyncTestDispatch = dispatch;
  }
  const fetchDesignsInFlight = useRef(null);
  const fetchLinkedAgentsInFlight = useRef(null);

  // Ably `dispatch_progress` events are INFORMATIONAL ONLY: they drive the
  // per-(design, agent) progress overlay and nothing else. Completion is decided
  // by the n8n webhook HTTP response in dispatchOne — a 200 on the final phase is
  // the authoritative "done" signal (both webhooks resolve in ~6–10s). We do NOT
  // gate completion on Ably; the server's `dispatch_progress:done` re-broadcast
  // can go missing (it never reached the client in the live capture — see the
  // separate finding), whereas the HTTP status always arrives.
  const processDispatchProgress = useCallback((data, fallbackAgentId) => {
    const designId = data?.design_id;
    const agentId = data?.agent_id || fallbackAgentId;
    if (!designId || !agentId) return;
    const common = {
      designId,
      agentId,
      stage: data.stage,
      stageLabel: data.stage_label,
      stageIndex: data.stage_index,
      stageTotal: data.stage_total,
      detail: data.detail ?? null,
      // Tags every Ably-sourced write so the reducer can drop it once the pair
      // is resolved by the HTTP response. Ably is informational, never terminal.
      fromAbly: true,
    };
    if (data.stage === 'failed') {
      dispatch({ type: 'DISPATCH_FAILED', payload: { ...common, errorMessage: data.error_message, failedAtStage: data.failed_at_stage } });
    } else if (data.stage === 'done') {
      dispatch({ type: 'DISPATCH_DONE', payload: common });
    } else {
      dispatch({ type: 'DISPATCH_PROGRESS_UPDATE', payload: common });
    }
  }, []);

  // Only `dispatch_progress` events drive the overlay; the Media Station's own
  // untyped `{status,...}` replies are ignored here (they are HTTP-response
  // information, surfaced through the webhook return value, not the overlay).
  // `station_settings_state` (T042/T044) is the station's own report of the
  // library it will write to. It is keyed by the channel's agent id — the
  // subscription is station-scoped — with the payload's agentId as fallback.
  // `playlist_progress` (T240) is informational too: it refines the strip's
  // build stage, but only the deliver-playlist HTTP response ends a delivery.
  const playlistRef = useRef(state.playlist);
  playlistRef.current = state.playlist;

  const handleAblyMessage = useCallback((agentId, data) => {
    if (data?.type === 'playlist_progress') {
      const { id, delivery } = playlistRef.current;
      if (data.playlist_id && data.playlist_id === id && ['sending', 'building'].includes(delivery.status)) {
        dispatch({
          type: 'PLAYLIST_DELIVERY_UPDATE',
          payload: {
            progressStage: data.stage ?? null,
            progressLabel: data.stage_label ?? null,
            ...(data.correlation_id ? { correlationId: data.correlation_id } : {}),
          },
        });
      }
    } else if (data?.type === 'dispatch_progress') {
      processDispatchProgress(data, agentId);
    } else if (data?.type === IMPORT_QUEUE_STATE_TYPE) {
      const stationId = agentId || data.agentId;
      if (stationId) dispatch({ type: 'STATION_IMPORT_STATE', payload: { agentId: stationId, state: data } });
    } else if (data?.type === 'station_settings_state') {
      const stationId = agentId || data.agentId;
      if (stationId) {
        dispatch({ type: 'STATION_SETTINGS_STATE', payload: { agentId: stationId, state: data } });
      }
    }
  }, [processDispatchProgress]);

  // DEV-only handle so Cypress can drive the overlay exactly as the Ably
  // subscriber would (progress display only — completion is HTTP-driven).
  if (import.meta.env.DEV) {
    window.__turboSyncTestEmitMessage = (agentId, data) => handleAblyMessage(agentId, data);
  }

  /**
   * Mark a (designId, agentId) dispatch as failed so the card surfaces Retry.
   * Called on a terminal failure reply or an HTTP rejection (incl. timeout).
   */
  const markDispatchFailed = useCallback((designId, agentId, errorMessage) => {
    dispatch({
      type: 'DISPATCH_FAILED',
      payload: {
        designId, agentId, stage: 'failed',
        stageLabel: 'Failed', stageIndex: 0, stageTotal: 0,
        detail: null, errorMessage, failedAtStage: null,
      },
    });
  }, []);

  // Initialize API client with auth hook. AppProvider also wraps the public
  // sign-in/sign-up routes, so the webhook calls keyed on isApiReady must wait
  // for a signed-in session, not just for Clerk's getToken to exist.
  useEffect(() => {
    if (!isApiAuthReady({ getToken, isSignedIn })) {
      setIsApiReady(false);
      return;
    }
    api.setAuthHook({ getToken });
    setIsApiReady(true);
  }, [getToken, isSignedIn]);


const fetchDesigns = useCallback(async (options = {}) => {
    if (!isApiReady) {
      console.warn('API not ready, skipping fetchDesigns');
      return null;
    }
    if (fetchDesignsInFlight.current) {
      return fetchDesignsInFlight.current;
    }

    dispatch({ type: 'SET_DESIGNS_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    const inflight = (async () => {
      try {
        const result = await api.fetchDesigns(options);
        dispatch({ type: 'SET_DESIGNS', payload: result.designs });
        dispatch({ type: 'SET_DESIGNS_LAST_FETCHED', payload: Date.now() });
        return result;
      } catch (error) {
        console.error('Failed to fetch designs from canva:', error);
        if (error.code === 'canva_reauth_required') {
          dispatch({ type: 'SET_CANVA_REAUTH_REQUIRED' });
        } else {
          dispatch({ type: 'SET_ERROR', payload: error.message });
        }
        return null;
      }
    })();

    fetchDesignsInFlight.current = inflight;
    try {
      return await inflight;
    } finally {
      fetchDesignsInFlight.current = null;
    }
  }, [isApiReady]);

  /**
   * Fetch user's linked desktop agents
   * Includes online/offline status check
   */
  const fetchLinkedAgents = useCallback(async () => {
    if (!isApiReady) {
      console.warn('API not ready, skipping fetchLinkedAgents');
      return;
    }
    if (fetchLinkedAgentsInFlight.current) {
      return fetchLinkedAgentsInFlight.current;
    }

    dispatch({ type: 'SET_AGENTS_LOADING', payload: true });

    const inflight = (async () => {
      try {
        const { agents, plan } = await api.fetchLinkedAgents();
        dispatch({ type: 'SET_LINKED_AGENTS', payload: agents });
        dispatch({ type: 'SET_ORG_PLAN', payload: plan });
      } catch (error) {
        console.error('Failed to fetch agents:', error);
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    })();

    fetchLinkedAgentsInFlight.current = inflight;
    try {
      return await inflight;
    } finally {
      fetchLinkedAgentsInFlight.current = null;
    }
  }, [isApiReady]);

  /**
   * Link a new desktop agent to the user's account
   * 
   * @param {string} agentId - UUID of the desktop agent
   * @param {string} description - Optional description
   */
  const linkAgent = useCallback(async (agentId, description) => {
    try {
      const agent = await api.linkAgent(agentId, description);
      // The Ably token only covers the stations linked when it was minted;
      // re-authorize before the new station's channel gets subscribed.
      try {
        await ablyService.reauthorize();
      } catch (reauthError) {
        console.error('Failed to refresh the Ably token after linking a station:', reauthError);
      }
      dispatch({ type: 'ADD_AGENT', payload: agent });
      // Re-fetch all agents so the newly added station shows its real status
      await fetchLinkedAgents();
      return agent;
    } catch (error) {
      console.error('Failed to link agent:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [fetchLinkedAgents]);

  /**
   * Unlink a desktop agent from the user's account
   * 
   * @param {string} agentId - UUID of the desktop agent
   */
  const unlinkAgent = useCallback(async (agentId) => {
    try {
      await api.unlinkAgent(agentId);
      dispatch({ type: 'REMOVE_AGENT', payload: agentId });
    } catch (error) {
      console.error('Failed to unlink agent:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Send design assets to desktop agent
   * 
   * @param {string} designId - Canva design ID
   * @param {string} designTitle - Design title/name
   * @param {string} agentId - Desktop agent UUID
   */
  // Note: dispatch failures are surfaced per-card by dispatchOne (toast + Retry
  // affordance), so these two wrappers deliberately do NOT dispatch SET_ERROR.
  // With event-driven completion, the HTTP promise can still reject in the
  // background AFTER the card has already completed on the Ably `done` event
  // (e.g. a dropped long-held response times out); a global error banner in
  // that case would be a false alarm.
  const sendDesignToAgent = useCallback(async (designId, designTitle, agentId) => {
    try {
      return await api.sendDesignToAgent(designId, designTitle, agentId);
    } catch (error) {
      console.error('Failed to send design to agent:', error);
      throw error;
    }
  }, []);

  /**
   * Load design in ProPresenter via desktop agent
   * 
   * @param {string} designId - Canva design ID
   * @param {string} designTitle - Design title/name
   * @param {string} agentId - Desktop agent UUID
   */
  const loadInProPresenter = useCallback(async (designId, designTitle, agentId) => {
    try {
      return await api.loadInProPresenter(designId, designTitle, agentId);
    } catch (error) {
      console.error('Failed to load in ProPresenter:', error);
      throw error;
    }
  }, []);

  const checkDesignStatus = useCallback(async (designId, designTitle, agentId) => {
    try {
      return await api.checkDesignStatus(designId, designTitle, agentId);
    } catch (error) {
      console.error('Failed to check design status:', error);
      throw error;
    }
  }, []);

  /**
   * @deprecated Use sendDesignToAgent or loadInProPresenter instead
   */
  const sendToProPresenter = useCallback(async (designId, designTitle, agentId) => {
    return loadInProPresenter(designId, designTitle, agentId);
  }, [loadInProPresenter]);

  /**
   * Toggle selection of a design
   * 
   * @param {string} designId - Design ID to toggle
   */
  const toggleDesignSelection = useCallback((designId) => {
    dispatch({ type: 'TOGGLE_DESIGN_SELECTION', payload: designId });
  }, []);

  /**
   * Select all designs
   */
  const selectAllDesigns = useCallback(() => {
    dispatch({ type: 'SELECT_ALL_DESIGNS' });
  }, []);

  /**
   * Clear all design selections
   */
  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);

  const markDesignCompleted = useCallback((designId) => {
    dispatch({ type: 'ADD_COMPLETED_DESIGN', payload: designId });

    setTimeout(() => {
      dispatch({ type: 'REMOVE_COMPLETED_DESIGN', payload: designId });
    }, 5000);
  }, []);

  /**
   * Clear dispatch-progress entries for a (design, agent) pair, or for a whole
   * design if `agentId` is omitted. Used by the design card "retry" / dismiss
   * affordance and by the auto-clear-after-success path.
   */
  const clearDispatchProgress = useCallback((designId, agentId) => {
    dispatch({ type: 'CLEAR_DISPATCH_PROGRESS', payload: { designId, agentId } });
  }, []);

  /**
   * Seed an optimistic `queued` dispatch-progress entry so the design card
   * renders the in-progress overlay immediately on click — without waiting
   * for the first Ably `dispatch_progress` event to arrive (n8n's validation
   * + setup nodes add 1–3s before the first emit).
   *
   * The entry is overwritten by the first real event from the workflow.
   */
  const markDispatchQueued = useCallback((designId, agentId) => {
    // Starting a fresh dispatch: lift any prior terminal lock (Retry / re-send)
    // so this attempt's own progress events are applied again.
    dispatch({ type: 'DISPATCH_UNRESOLVE', payload: { designId, agentId } });
    dispatch({
      type: 'DISPATCH_PROGRESS_UPDATE',
      payload: {
        designId,
        agentId,
        stage: 'queued',
        stageLabel: 'Queueing dispatch…',
        stageIndex: 0,
        stageTotal: 3,
        detail: null,
      },
    });
  }, []);

  /**
   * Mark a (designId, agentId) dispatch as RESOLVED by the authoritative webhook
   * HTTP response. After this, informational Ably events for the pair can no
   * longer re-create or override its terminal state (see the reducer guard).
   */
  const markDispatchResolved = useCallback((designId, agentId) => {
    dispatch({ type: 'DISPATCH_RESOLVED', payload: { designId, agentId } });
  }, []);

  /**
   * Load the user's auto-dispatch configuration
   */
  const fetchAutoDispatch = useCallback(async () => {
    if (!isApiReady) return;
    dispatch({ type: 'SET_AUTO_DISPATCH_LOADING', payload: true });
    try {
      const { canvaAuthFailure, plan, ...config } = await api.fetchUserAutoDispatch();
      dispatch({ type: 'SET_AUTO_DISPATCH', payload: config });
      dispatch({ type: 'SET_ORG_PLAN', payload: plan });
      dispatch({ type: 'SET_CANVA_AUTH_FAILURE', payload: canvaAuthFailure });
    } catch (error) {
      console.error('Failed to fetch auto-dispatch config:', error);
      dispatch({ type: 'SET_AUTO_DISPATCH_LOADING', payload: false });
    }
  }, [isApiReady]);

  /**
   * Save the user's auto-dispatch schedule
   *
   * @param {{enabled: boolean, schedule: object|null}} config
   */
  const setAutoDispatchSchedule = useCallback(async (config) => {
    try {
      await api.setAutoDispatchSchedule(config);
      dispatch({ type: 'SET_AUTO_DISPATCH', payload: config });
    } catch (error) {
      console.error('Failed to save auto-dispatch schedule:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Trigger detect-then-dispatch immediately for this user
   */
  const triggerSyncNow = useCallback(async () => {
    dispatch({ type: 'SET_SYNCING_NOW', payload: true });
    try {
      await api.triggerSyncNow();
    } catch (error) {
      console.error('Failed to trigger sync now:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      dispatch({ type: 'SET_SYNCING_NOW', payload: false });
    }
  }, []);

  /**
   * Clear is_new / is_updated flags for the given designs.
   * Optimistic local update + fire-and-forget API call.
   *
   * @param {string[]} designIds
   */
  const markDesignsSeen = useCallback(async (designIds) => {
    if (!designIds || designIds.length === 0) return;
    dispatch({ type: 'CLEAR_DESIGN_FLAGS_LOCAL', payload: designIds });
    try {
      await api.markDesignsSeen(designIds);
    } catch (error) {
      console.error('Failed to mark designs seen:', error);
    }
  }, []);

  /**
   * Load per-Media Station settings. Called by the Media Stations page on
   * mount (not on every dashboard load). A 404 means the Station Settings workflow
   * isn't active yet: degrade to 'not_configured', no global error.
   */
  // ms-pp21-22 L4: the strip's "Import now", relayed to the station.
  const requestImportNow = useCallback((agentId) => api.requestImportNow(agentId), []);

  const fetchStationSettings = useCallback(async () => {
    if (!isApiReady) return;
    dispatch({ type: 'SET_STATION_SETTINGS_STATUS', payload: 'loading' });
    try {
      const stations = await api.fetchStationSettings();
      dispatch({ type: 'SET_STATION_SETTINGS', payload: stations });
    } catch (error) {
      if (error.status === 404) {
        dispatch({ type: 'SET_STATION_SETTINGS_STATUS', payload: 'not_configured' });
      } else {
        console.error('Failed to fetch station settings:', error);
        dispatch({ type: 'SET_STATION_SETTINGS_STATUS', payload: 'error' });
      }
    }
  }, [isApiReady]);

  /**
   * Set (or clear, with null) the ProPresenter library for one Media Station.
   * The outcome is recorded per station in `stationSettings[agentId].lastSave`
   * for the card's status line; failures are rethrown for the caller.
   *
   * @param {string} agentId
   * @param {string|null} libraryName - null reverts the station to its default
   */
  const setStationLibrary = useCallback(async (agentId, libraryName) => {
    try {
      const result = await api.setStationSettings(agentId, { library_name: libraryName });
      const savedName = result?.settings && 'library_name' in result.settings
        ? result.settings.library_name
        : libraryName;
      dispatch({
        type: 'STATION_SETTINGS_SAVED',
        payload: {
          agentId,
          libraryName: savedName ?? null,
          settingsUpdatedAt: result?.settings_updated_at ?? null,
          lastSave: {
            at: Date.now(),
            libraryName: savedName ?? null,
            delivered: result?.delivered === true,
            // 'success' | 'failure' | null — the station's ack status (W1 `ack_status`)
            ackStatus: result?.ack_status ?? null,
            correlationId: result?.correlation_id ?? null,
            error: null,
          },
          station: result?.station_state ?? null,
        },
      });
      return result;
    } catch (error) {
      console.error('Failed to save station library:', error);
      dispatch({
        type: 'STATION_SETTINGS_SAVED',
        payload: {
          agentId,
          lastSave: { at: Date.now(), libraryName, delivered: false, correlationId: null, error: error.message },
        },
      });
      throw error;
    }
  }, []);

  // Load auto-dispatch config once when API is ready (app-wide)
  const autoDispatchLoadedRef = useRef(false);
  useEffect(() => {
    if (isApiReady && !autoDispatchLoadedRef.current) {
      autoDispatchLoadedRef.current = true;
      fetchAutoDispatch();
    }
  }, [isApiReady, fetchAutoDispatch]);

  // ---- Ably: initialize once, then maintain per-agent subscriptions ----
  // Per plan §4.3: dispatch_progress events arrive on each agent's
  // `turbosync:agent-{agent_id}:response` channel. We must filter by
  // `type === 'dispatch_progress'` because that channel also carries the
  // agent's existing terminal responses (which lack a type discriminator).
  const ablyInitRef = useRef({ started: false, ready: false });

  useEffect(() => {
    if (!isApiReady || ablyInitRef.current.started) return;
    ablyInitRef.current.started = true;

    let cancelled = false;
    (async () => {
      try {
        const token = await api.getAblyToken();
        if (cancelled) return;
        ablyService.initialize(token, () => api.getAblyToken(), () => {
          if (!cancelled) ablyInitRef.current.ready = true;
        });
      } catch (err) {
        console.error('Failed to initialize Ably for dispatch progress:', err);
        ablyInitRef.current.started = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isApiReady]);

  // Subscribe to each linked agent's response channel; reconcile on agent-list change.
  const subscribedAgentIdsRef = useRef(new Set());
  useEffect(() => {
    if (!isApiReady) return;

    const handleAgentMessage = (agentId) => (message) => {
      // Route both shapes: typed dispatch_progress (overlay) and the Media
      // Station's untyped status reply (completion). See handleAblyMessage.
      handleAblyMessage(agentId, message?.data);
    };

    let pollId = null;
    const reconcile = () => {
      if (!ablyInitRef.current.ready) return;
      const desired = new Set((state.linkedAgents || []).map(a => a.agent_id).filter(Boolean));
      const subscribed = subscribedAgentIdsRef.current;

      desired.forEach(agentId => {
        if (!subscribed.has(agentId)) {
          ablyService.subscribeToAgent(agentId, handleAgentMessage(agentId));
          subscribed.add(agentId);
        }
      });
      subscribed.forEach(agentId => {
        if (!desired.has(agentId)) {
          ablyService.unsubscribeFromAgent(agentId);
          subscribed.delete(agentId);
        }
      });
    };

    // Try immediately, and if Ably hasn't connected yet, poll briefly until it has.
    reconcile();
    if (!ablyInitRef.current.ready) {
      pollId = setInterval(() => {
        if (ablyInitRef.current.ready) {
          reconcile();
          clearInterval(pollId);
          pollId = null;
        }
      }, 250);
    }

    return () => {
      if (pollId) clearInterval(pollId);
    };
  }, [isApiReady, state.linkedAgents]);

  // Completion ("Sent!" overlay) is driven by dispatchOne in the Dashboard
  // after the full pipeline's API awaits resolve — NOT by Ably `done` events.
  // The prior Ably-driven auto-complete raced with chained Send→Load dispatches:
  // Send's `done` arrived, fired markDesignCompleted + scheduled a clear, then
  // Load's events re-entered the in-flight state and the scheduled clear fired
  // mid-pipeline, causing the "Sent!" overlay to flash ~3 times. Ably events
  // still drive the per-stage progress overlay; only the terminal flip is
  // deterministic from the API completion. Regression guard:
  // cypress/e2e/dispatch-stage-progression.feature.

  /**
   * Update user settings
   *
   * @param {object} newSettings - Settings to update
   */
  const updateSettings = useCallback(async (newSettings) => {
    try {
      await api.updateSettings(newSettings);
      dispatch({ type: 'UPDATE_SETTINGS', payload: newSettings });
    } catch (error) {
      console.error('Failed to update settings:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const clearCanvaReauth = useCallback(() => {
    dispatch({ type: 'CLEAR_CANVA_REAUTH' });
  }, []);

  // The hydrated draft is not written back, so its savedAt keeps dating the restore banner.
  const hydratedPlaylistRef = useRef(state.playlist);
  useEffect(() => {
    if (state.playlist === hydratedPlaylistRef.current) return;
    try {
      window.localStorage.setItem(PLAYLIST_DRAFT_KEY, serializeDraft(state.playlist));
    } catch (error) {
      console.error('Failed to save the playlist draft:', error);
    }
  }, [state.playlist]);

  const addToPlaylist = useCallback((design) => {
    const item = createPlaylistItem(design);
    dispatch({ type: 'PLAYLIST_ADD_ITEM', payload: { item, playlistId: newPlaylistId() } });
    return item;
  }, []);

  const removeFromPlaylist = useCallback((itemKey) => {
    dispatch({ type: 'PLAYLIST_REMOVE_ITEM', payload: { itemKey } });
  }, []);

  const restorePlaylistItem = useCallback((item, index) => {
    dispatch({ type: 'PLAYLIST_RESTORE_ITEM', payload: { item, index } });
  }, []);

  const movePlaylistItem = useCallback((from, to) => {
    dispatch({ type: 'PLAYLIST_MOVE_ITEM', payload: { from, to } });
  }, []);

  const setPlaylistOrder = useCallback((itemKeys) => {
    dispatch({ type: 'PLAYLIST_SET_ORDER', payload: { itemKeys } });
  }, []);

  const setPlaylistMeta = useCallback((meta) => {
    dispatch({ type: 'PLAYLIST_SET_META', payload: meta });
  }, []);

  const clearPlaylist = useCallback(() => {
    dispatch({ type: 'PLAYLIST_CLEAR', payload: { playlist: createEmptyPlaylist() } });
  }, []);

  const dismissRestoredPlaylist = useCallback(() => {
    dispatch({ type: 'PLAYLIST_DISMISS_RESTORED' });
  }, []);

  const dismissInterruptedDelivery = useCallback(() => {
    dispatch({ type: 'PLAYLIST_DISMISS_INTERRUPTED' });
  }, []);

  const deliveryActive = DELIVERY_ACTIVE.includes(state.playlist.delivery.status);
  useEffect(() => {
    try {
      if (deliveryActive) {
        window.localStorage.setItem(PLAYLIST_DELIVERING_KEY, '1');
        dispatch({ type: 'PLAYLIST_DISMISS_INTERRUPTED' });
      } else {
        window.localStorage.removeItem(PLAYLIST_DELIVERING_KEY);
      }
    } catch {
      // Private mode: B-2 simply can't show after a reload.
    }
  }, [deliveryActive]);

  // Delivery (T240 Lane 06, spec §3.2). Items are sent one by one, in order,
  // through send-designs-to-agent (as dispatchOne does); only when all have
  // arrived does deliver-playlist combine them. The HTTP responses decide
  // every transition; Ably only animates the tiles and the strip.
  const linkedAgentsRef = useRef(state.linkedAgents);
  linkedAgentsRef.current = state.linkedAgents;
  const deliveryRunningRef = useRef(false);

  const updateDelivery = useCallback((changes) => {
    dispatch({ type: 'PLAYLIST_DELIVERY_UPDATE', payload: changes });
  }, []);

  const failDelivery = useCallback((kind, { item, index, missingItemKeys = [], error, ...rest }) => {
    const { items } = playlistRef.current;
    const agentId = playlistRef.current.delivery.agentId;
    updateDelivery({
      status: 'failed',
      failedKind: kind,
      missingItemKeys,
      errorMessage: failureLine(kind, {
        title: item?.title,
        k: index + 1,
        n: items.length,
        station: stationLabel(linkedAgentsRef.current, agentId),
        missing: missingItemKeys.length,
      }),
      errorDetail: error?.message ?? null,
      currentKey: null,
      ...rest,
    });
  }, [updateDelivery]);

  const buildPlaylist = useCallback(async (agentId) => {
    const playlist = playlistRef.current;
    updateDelivery({
      status: 'building',
      agentId,
      itemIndex: playlist.items.length,
      itemTotal: playlist.items.length,
      currentKey: null,
      pendingKeys: [],
      failedKind: null,
      failedKey: null,
      missingItemKeys: [],
      errorMessage: null,
      errorDetail: null,
      progressStage: null,
      progressLabel: null,
    });
    try {
      const result = await api.deliverPlaylist(agentId, playlist);
      updateDelivery({
        status: 'done',
        proFilePath: result?.pro_file_path ?? null,
        groupCount: result?.group_count ?? playlist.items.length,
        correlationId: result?.correlation_id ?? null,
        deliveredAt: Date.now(),
        edited: false,
      });
    } catch (error) {
      const { kind, missingItemKeys } = classifyBuildError(error, playlist.items);
      failDelivery(kind, { missingItemKeys, error });
    }
  }, [updateDelivery, failDelivery]);

  const runDelivery = useCallback(async (agentId, sendKeys) => {
    if (deliveryRunningRef.current) return;
    deliveryRunningRef.current = true;
    try {
      const { items } = playlistRef.current;
      const queue = items.filter((item) => sendKeys.includes(item.itemKey));
      updateDelivery({
        status: 'sending',
        agentId,
        itemIndex: 0,
        itemTotal: items.length,
        pendingKeys: queue.map((item) => item.itemKey),
        failedKind: null,
        failedKey: null,
        missingItemKeys: [],
        errorMessage: null,
        errorDetail: null,
        progressStage: null,
        progressLabel: null,
        edited: false,
      });

      for (let q = 0; q < queue.length; q += 1) {
        const item = queue[q];
        const index = items.indexOf(item);
        updateDelivery({
          currentKey: item.itemKey,
          itemIndex: index,
          pendingKeys: queue.slice(q).map((i) => i.itemKey),
        });
        markDispatchQueued(item.designId, agentId);
        try {
          await api.sendDesignToAgent(item.designId, item.title, agentId);
          markDispatchResolved(item.designId, agentId);
          clearDispatchProgress(item.designId, agentId);
        } catch (error) {
          markDispatchResolved(item.designId, agentId);
          markDispatchFailed(item.designId, agentId, error.message);
          const kind = classifySendError(error, isStationOnline(linkedAgentsRef.current, agentId));
          failDelivery(kind, { item, index, error, failedKey: item.itemKey });
          return;
        }
      }

      await buildPlaylist(agentId);
    } finally {
      deliveryRunningRef.current = false;
    }
  }, [updateDelivery, failDelivery, buildPlaylist, markDispatchQueued, markDispatchResolved, markDispatchFailed, clearDispatchProgress]);

  const openDeliverConfirmation = useCallback(() => {
    const { items, delivery } = playlistRef.current;
    if (items.length === 0 || DELIVERY_ACTIVE.includes(delivery.status) || delivery.status === 'confirming') return;
    updateDelivery({ status: 'confirming', returnStatus: delivery.status });
  }, [updateDelivery]);

  const cancelDeliverConfirmation = useCallback(() => {
    const { delivery } = playlistRef.current;
    if (delivery.status !== 'confirming') return;
    updateDelivery({ status: delivery.returnStatus ?? 'idle', returnStatus: null });
  }, [updateDelivery]);

  const deliverPlaylist = useCallback(async (agentId) => {
    const { items } = playlistRef.current;
    if (items.length === 0 || !isStationOnline(linkedAgentsRef.current, agentId)) return;
    dispatch({ type: 'PLAYLIST_SET_META', payload: { targetAgentId: agentId } });
    updateDelivery({ returnStatus: null, deliveredAt: null });
    await runDelivery(agentId, items.map((item) => item.itemKey));
  }, [updateDelivery, runDelivery]);

  const retryPlaylistDelivery = useCallback(async () => {
    const { delivery } = playlistRef.current;
    if (delivery.status !== 'failed' || deliveryRunningRef.current) return;
    const mode = retryModeFor(delivery.failedKind);
    if (mode === 'resume') await runDelivery(delivery.agentId, delivery.pendingKeys);
    else if (mode === 'missing') await runDelivery(delivery.agentId, delivery.missingItemKeys);
    else if (mode === 'rebuild') {
      deliveryRunningRef.current = true;
      try {
        await buildPlaylist(delivery.agentId);
      } finally {
        deliveryRunningRef.current = false;
      }
    } else if (mode === 'choose') openDeliverConfirmation();
  }, [runDelivery, buildPlaylist, openDeliverConfirmation]);

  // Saved playlists (T240 Lane 08, spec S8). The draft in localStorage stays
  // the working copy; these calls keep a named copy per playlist on the server.
  const savedPlaylistsRef = useRef(state.savedPlaylists);
  savedPlaylistsRef.current = state.savedPlaylists;
  const savedStatusRef = useRef(state.savedPlaylistsStatus);
  savedStatusRef.current = state.savedPlaylistsStatus;

  const noteNotConfigured = useCallback((error) => {
    if (error?.code !== 'not_configured') return false;
    dispatch({ type: 'SET_SAVED_PLAYLISTS_STATUS', payload: 'not_configured' });
    return true;
  }, []);

  const fetchPlaylists = useCallback(async () => {
    if (!isApiReady) return;
    if (savedPlaylistsRef.current.length === 0) dispatch({ type: 'SET_SAVED_PLAYLISTS_STATUS', payload: 'loading' });
    try {
      dispatch({ type: 'SET_SAVED_PLAYLISTS', payload: await api.fetchPlaylists() });
    } catch (error) {
      if (!noteNotConfigured(error)) {
        console.error('Failed to fetch saved playlists:', error);
        dispatch({ type: 'SET_SAVED_PLAYLISTS_STATUS', payload: 'error' });
      }
    }
  }, [isApiReady, noteNotConfigured]);

  /**
   * Save the draft. Resolves `{ ok: true, row }`, or `{ ok: false, reason }`
   * with reason `conflict` (the saved copy is newer than the one this draft
   * came from; SV-9), `playlist_not_found` (deleted elsewhere; offer Save as
   * new), `not_configured`, `empty` or `error` (with `error`).
   *
   * @param {{ status?: 'delivered', lastDeliveredAt?: string, targetAgentId?: string, force?: boolean, asNew?: boolean }} [options]
   */
  const savePlaylist = useCallback(async ({ status, lastDeliveredAt, targetAgentId, force = false, asNew = false } = {}) => {
    const basis = playlistRef.current;
    if (basis.items.length === 0) return { ok: false, reason: 'empty' };
    if (savedStatusRef.current === 'not_configured') return { ok: false, reason: 'not_configured' };
    const draft = asNew ? { ...basis, ...UNSAVED } : basis;
    const known = draft.savedUpdatedAt && savedPlaylistsRef.current.find((p) => p.id === draft.id);
    if (!force && known?.updated_at && known.updated_at > draft.savedUpdatedAt) return { ok: false, reason: 'conflict' };
    try {
      const row = await api.savePlaylist(buildSavePlaylistPayload(draft, {
        status,
        lastDeliveredAt,
        targetAgentId: targetAgentId ?? draft.targetAgentId,
      }));
      if (!row?.id) throw Object.assign(new Error('The save returned no playlist.'), { code: 'upstream_error' });
      dispatch({ type: 'PLAYLIST_MARK_SAVED', payload: { basis, row } });
      dispatch({ type: 'UPSERT_SAVED_PLAYLIST', payload: row });
      if (savedStatusRef.current !== 'ready') dispatch({ type: 'SET_SAVED_PLAYLISTS_STATUS', payload: 'ready' });
      return { ok: true, row };
    } catch (error) {
      if (noteNotConfigured(error)) return { ok: false, reason: 'not_configured' };
      if (error.code === 'playlist_not_found') return { ok: false, reason: 'playlist_not_found' };
      console.error('Failed to save the playlist:', error);
      return { ok: false, reason: 'error', error };
    }
  }, [noteNotConfigured]);

  // Replaces the draft. The caller confirms first when the draft is dirty (SV-8).
  const openPlaylist = useCallback((id) => {
    const row = savedPlaylistsRef.current.find((p) => p.id === id);
    if (!row || DELIVERY_ACTIVE.includes(playlistRef.current.delivery.status)) return null;
    const playlist = playlistFromSaved(row);
    dispatch({ type: 'PLAYLIST_SET', payload: { playlist } });
    return playlist;
  }, []);

  const deletePlaylist = useCallback(async (id) => {
    try {
      await api.deletePlaylist(id);
    } catch (error) {
      if (noteNotConfigured(error)) return { ok: false, reason: 'not_configured' };
      if (error.code !== 'playlist_not_found') {
        console.error('Failed to delete the playlist:', error);
        return { ok: false, reason: 'error', error };
      }
    }
    dispatch({ type: 'REMOVE_SAVED_PLAYLIST', payload: id });
    dispatch({ type: 'PLAYLIST_MARK_UNSAVED', payload: { id } });
    return { ok: true };
  }, [noteNotConfigured]);

  // The caller confirms first when the draft is dirty.
  const newPlaylist = useCallback(() => {
    dispatch({ type: 'PLAYLIST_CLEAR', payload: { playlist: createEmptyPlaylist() } });
  }, []);

  // A delivered saved playlist records it: status, station and time (spec S8 row).
  const deliveryStatus = state.playlist.delivery.status;
  const prevDeliveryStatusRef = useRef(deliveryStatus);
  useEffect(() => {
    const previous = prevDeliveryStatusRef.current;
    prevDeliveryStatusRef.current = deliveryStatus;
    if (deliveryStatus !== 'done' || previous === 'done') return;
    const { savedUpdatedAt, delivery } = playlistRef.current;
    if (!savedUpdatedAt) return;
    savePlaylist({
      status: 'delivered',
      lastDeliveredAt: new Date(delivery.deliveredAt ?? Date.now()).toISOString(),
      targetAgentId: delivery.agentId ?? undefined,
      force: true,
    });
  }, [deliveryStatus, savePlaylist]);

  // Context value
  const value = {
    state,
    isApiReady,
    dispatch,
    // Actions
    fetchDesigns,
    fetchLinkedAgents,
    linkAgent,
    unlinkAgent,
    sendDesignToAgent,
    loadInProPresenter,
    checkDesignStatus,
    sendToProPresenter,
    toggleDesignSelection,
    selectAllDesigns,
    clearSelection,
    markDesignCompleted,
    clearDispatchProgress,
    markDispatchQueued,
    markDispatchResolved,
    markDispatchFailed,
    updateSettings,
    fetchAutoDispatch,
    setAutoDispatchSchedule,
    triggerSyncNow,
    markDesignsSeen,
    fetchStationSettings,
    requestImportNow,
    setStationLibrary,
    clearError,
    clearCanvaReauth,
    addToPlaylist,
    removeFromPlaylist,
    restorePlaylistItem,
    movePlaylistItem,
    setPlaylistOrder,
    setPlaylistMeta,
    clearPlaylist,
    dismissRestoredPlaylist,
    dismissInterruptedDelivery,
    openDeliverConfirmation,
    cancelDeliverConfirmation,
    deliverPlaylist,
    retryPlaylistDelivery,
    fetchPlaylists,
    savePlaylist,
    openPlaylist,
    deletePlaylist,
    newPlaylist,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/**
 * Custom hook to use the App Context
 * Must be used within AppProvider
 */
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}



